import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from '../i18n/i18n';
import { Search } from 'lucide-react';
import {
  BarChart3, UserStarIcon, Package, ShoppingCart, Calendar,
  Settings, DollarSign, Receipt, PieChart, MessageSquare, Truck,
  Tag, X, User
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';


const SearchBar = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const navigate = useNavigate();
  // Toutes les sections disponibles avec termes de recherche multilingues
  const allSections = useMemo(() => [
    {
      id: 'dashboard',
      label: t('dashboard'),
      icon: BarChart3,
      category: t('main'),
      searchTerms: ['dashboard', 'tableau', 'panel', 'tablero', 'main', 'principal']
    },
    {
      id: 'users',
      label: t('users'),
      icon: User,
      category: t('management'),
      searchTerms: ['users', 'utilisateurs', 'usuarios', 'user', 'utilisateur', 'usuario']
    },
        {
      id: 'customers',
      label: t('customers'),
      icon: UserStarIcon,
      category: t('management'),
      searchTerms: ['customers', 'clients', 'clientes', 'customer', 'client', 'cliente']
    },
    {
      id: 'suppliers',
      label: t('suppliers'),
      icon: Truck,
      category: t('management'),
      searchTerms: ['suppliers', 'fournisseurs', 'proveedores', 'vendor', 'vendeur', 'provider']
    },
    {
      id: 'products',
      label: t('products'),
      icon: Package,
      category: t('management'),
      searchTerms: ['products', 'produits', 'productos', 'item', 'article']
    },
    {
      id: 'promotions',
      label: t('promotions'),
      icon: Tag,
      category: t('management'),
      searchTerms: ['promotions', 'promos', 'promociones', 'discount', 'réduction', 'descuento']
    },
    {
      id: 'reservations',
      label: t('reservations'),
      icon: Calendar,
      category: t('management'),
      searchTerms: ['reservations', 'réservations', 'reservas', 'booking', 'bookings']
    },
    {
      id: 'exchange',
      label: t('exchangeRate'),
      icon: DollarSign,
      category: t('finance'),
      searchTerms: ['exchange', 'taux', 'rate', 'cambio', 'currency', 'devise', 'moneda']
    },
    {
      id: 'sales',
      label: t('sales'),
      icon: ShoppingCart,
      category: t('finance'),
      searchTerms: ['sales', 'ventes', 'ventas', 'sell', 'vendre', 'vender']
    },
    {
      id: 'invoices',
      label: t('invoices'),
      icon: Receipt,
      category: t('finance'),
      searchTerms: ['invoices', 'facturation', 'facturación', 'invoice', 'facture', 'factura']
    },
    {
      id: 'statistics',
      label: t('statistics'),
      icon: PieChart,
      category: t('finance'),
      searchTerms: ['statistics', 'statistiques', 'estadísticas', 'stats', 'analytics']
    },
    {
      id: 'admin-support',
      label: t('adminSupport'),
      icon: MessageSquare,
      category: t('support'),
      searchTerms: ['admin', 'support', 'soporte', 'aide', 'help', 'ayuda']
    },
    {
      id: 'supplier-support',
      label: t('supplierSupport'),
      icon: MessageSquare,
      category: t('support'),
      searchTerms: ['supplier', 'fournisseur', 'proveedor', 'support', 'soporte', 'aide']
    },
    {
      id: 'settings',
      label: t('settings'),
      icon: Settings,
      category: t('settings'),
      searchTerms: ['settings', 'paramètres', 'configuración', 'configuration', 'preferences']
    }
  ], [t]); // Dépend de t pour se recalculer quand la langue change

  const handleSearchChange = useCallback((e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(e.target.value);

    if (query.trim() === '') {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Filtrer les suggestions avec recherche multilingue
    const filtered = allSections.filter(section =>
      section.label.toLowerCase().includes(query) ||
      section.category.toLowerCase().includes(query) ||
      section.searchTerms.some(term => term.toLowerCase().includes(query))
    );

    setFilteredSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
  }, [allSections]);

  const handleSuggestionClick = useCallback((sectionId) => {
    navigate(`/${sectionId}`);
    setSearchQuery('');
    setShowSuggestions(false);
  }, [navigate]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && filteredSuggestions.length > 0) {
      handleSuggestionClick(filteredSuggestions[0].id);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSearchQuery('');
    }
  }, [filteredSuggestions, handleSuggestionClick]);

  return (
    <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: '500px', minWidth: '20px' }}>     
     <div style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder={t('searchSectionPlaceholder')}
        value={searchQuery}
        onChange={handleSearchChange}
        onFocus={() => searchQuery && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        onKeyDown={handleKeyDown}
        style={{
          width: '100%',
          padding: '10px 40px 10px 40px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          fontSize: '14px',
          outline: 'none',
          transition: 'all 0.2s ease'
        }}
        onFocusCapture={(e) => {
          e.target.style.borderColor = '#7c3aed';
          e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
        }}
        onBlurCapture={(e) => {
          e.target.style.borderColor = '#e5e7eb';
          e.target.style.boxShadow = 'none';
        }}
      />
      <Search
        size={18}
        style={{
          position: 'absolute',
          left: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#9ca3af'
        }}
      />
      {searchQuery && (
        <button
          onClick={() => {
            setSearchQuery('');
            setShowSuggestions(false);
          }}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            padding: '4px'
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>

      {/* Suggestions dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          maxHeight: '400px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          {filteredSuggestions.map((suggestion, idx) => {
            const Icon = suggestion.icon;
            return (
              <button
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion.id)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  borderBottom: idx < filteredSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                  textAlign: 'left',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  background: '#f5f3ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon size={16} color="#7c3aed" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                    {suggestion.label}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    {suggestion.category}
                  </div>
                </div>
                <div style={{
                  padding: '2px 8px',
                  background: '#f3f4f6',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#6b7280'
                }}>
                  {t('enter')} ↵
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchBar;